import { Link } from '@tanstack/react-router';
import {
  Banner,
  Button,
  Card,
  CardContent,
  CollapsibleSection,
  EmptyState,
  ListCard,
  ListCardRow,
  LoadingOrbit,
  Markdown,
  MetadataItem,
  Page,
  PageHeader,
  StatusBadge,
  TagBadge,
  Text,
  useToast,
} from '@chatsdet/ui';
import { ArrowLeft, Compass, WarningCircle } from '@phosphor-icons/react';
import { usePropertySummaries } from '../../../hooks/usePropertyMetadata';
import { formatDurationFromIso } from '../../../lib/formatDuration';
import {
  useExplorationCycle,
  useCancelExplorationCycle,
  isActiveCycleStatus,
  type ExplorationAssignment,
  type ExplorationAssignmentTarget,
} from '../useExploration';

type Props = { cycleId: string };

/** A cycle is stale once it's been active well past a normal run's length. */
const STALE_CYCLE_MS = 30 * 60 * 1000;

function cycleBadgeStatus(status: string): 'success' | 'failed' | 'running' {
  if (status === 'completed') return 'success';
  if (status === 'failed') return 'failed';
  return 'running';
}

function assignmentBadgeStatus(status: string): 'success' | 'failed' | 'running' {
  if (status === 'completed') return 'success';
  if (status === 'failed') return 'failed';
  return 'running';
}

function targetLabel(target: ExplorationAssignmentTarget): string {
  const kindLabel = target.kind === 'area' ? 'Area' : target.kind === 'lead' ? 'Lead' : 'Seed';
  return target.label ? `${kindLabel} · ${target.label}` : kindLabel;
}

const leadStatusColor: Record<string, 'blue' | 'gray'> = {
  open: 'blue',
  explored: 'gray',
  dismissed: 'gray',
};

function formatDate(iso?: string | null) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function CancelCycleButton({ cycleId }: { cycleId: string }) {
  const cancel = useCancelExplorationCycle();
  const { toast } = useToast();

  const handleCancel = () => {
    if (!window.confirm('Cancel this exploration cycle? Any workers still running will be abandoned.')) {
      return;
    }
    cancel.mutate(cycleId, {
      onSuccess: (result) => {
        toast.success(
          result.workersAbandoned > 0
            ? `Cycle cancelled — ${result.workersAbandoned} worker${result.workersAbandoned === 1 ? '' : 's'} abandoned.`
            : 'Cycle cancelled.'
        );
      },
      onError: (err) => {
        toast.error(err instanceof Error ? err.message : 'Failed to cancel cycle');
      },
    });
  };

  return (
    <Button variant="destructive" size="sm" onPress={handleCancel} isPending={cancel.isPending}>
      Cancel cycle
    </Button>
  );
}

function AssignmentRow({ assignment, index }: { assignment: ExplorationAssignment; index: number }) {
  return (
    <div className="px-4">
      <CollapsibleSection
        hideBorder={index === 0}
        header={
          <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2">
            <Text weight="medium" className="truncate">
              {assignment.persona?.name ?? 'No persona'}
            </Text>
            <StatusBadge status={assignmentBadgeStatus(assignment.status)} label={assignment.status} size="sm" />
            <Text size="xs" className="text-muted-foreground">
              {targetLabel(assignment.target)}
            </Text>
            {assignment.issues.length > 0 && (
              <TagBadge
                color="red"
                size="sm"
                hideDot
                name={`${assignment.issues.length} issue${assignment.issues.length === 1 ? '' : 's'}`}
              />
            )}
          </div>
        }
        actions={
          assignment.run?.exploratoryTestId ? (
            <Link
              to="/exploratory/$exploratoryTestId"
              params={{ exploratoryTestId: assignment.run.exploratoryTestId }}
              className="text-xs text-primary hover:underline"
            >
              View run →
            </Link>
          ) : undefined
        }
      >
        <div className="space-y-3 pl-1">
          {assignment.run?.summary && (
            <Text size="sm" className="whitespace-pre-wrap">
              {assignment.run.summary}
            </Text>
          )}
          <div>
            <Text size="xs" weight="semibold" className="mb-1 text-muted-foreground uppercase tracking-wide">
              Charter
            </Text>
            <div className="prose prose-sm max-w-none">
              <Markdown>{assignment.charterMd}</Markdown>
            </div>
          </div>
          {assignment.rationale && (
            <Text size="xs" className="text-muted-foreground">
              {assignment.rationale}
            </Text>
          )}
          {assignment.issues.length > 0 && (
            <ul className="list-disc space-y-0.5 pl-5">
              {assignment.issues.map((issue, i) => (
                <li key={i} className="text-xs">
                  {issue.title}
                  {issue.severity ? ` · ${issue.severity}` : ''}
                </li>
              ))}
            </ul>
          )}
        </div>
      </CollapsibleSection>
    </div>
  );
}

export function CycleDetailPage({ cycleId }: Props) {
  const q = useExplorationCycle(cycleId);
  const cycle = q.data?.cycle;
  const propertiesQ = usePropertySummaries();
  const propertyName = propertiesQ.data?.find((p) => p.id === cycle?.propertyId)?.name ?? null;

  if (q.isLoading) {
    return (
      <Page>
        <div className="flex items-center justify-center py-16">
          <LoadingOrbit size="lg" />
        </div>
      </Page>
    );
  }

  if (q.isError) {
    return (
      <Page>
        <PageHeader
          title="Cycle"
          eyebrow={
            <Link
              to="/product-map"
              search={{ view: 'explore' }}
              className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:underline"
            >
              <ArrowLeft size={14} /> Back to Explore
            </Link>
          }
        />
        <Banner variant="error">
          <Text size="sm">
            {q.error instanceof Error ? q.error.message : 'Failed to load this exploration cycle.'}
          </Text>
        </Banner>
      </Page>
    );
  }

  if (!cycle) {
    return (
      <Page>
        <PageHeader
          title="Cycle not found"
          eyebrow={
            <Link
              to="/product-map"
              search={{ view: 'explore' }}
              className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:underline"
            >
              <ArrowLeft size={14} /> Back to Explore
            </Link>
          }
        />
        <EmptyState icon={<Compass size={24} />} title="Cycle not found" description={`Cycle ${cycleId} was not found.`} />
      </Page>
    );
  }

  const isActive = isActiveCycleStatus(cycle.status);
  const isStale = isActive && Date.now() - new Date(cycle.startedAt).getTime() > STALE_CYCLE_MS;
  const doneCount = cycle.assignments.filter((a) => a.status !== 'running').length;

  return (
    <Page>
      <PageHeader
        eyebrow={
          <Link
            to="/product-map"
            search={{ view: 'explore' }}
            className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:underline"
          >
            <ArrowLeft size={14} /> Back to Explore
          </Link>
        }
        title={`Exploration - ${formatDate(cycle.startedAt)}`}
        description={
          <Text size="sm" className="text-muted-foreground">
            {cycle.id}
          </Text>
        }
        actions={
          <div className="flex items-center gap-2">
            <StatusBadge status={cycleBadgeStatus(cycle.status)} label={cycle.status} />
            {isActive && cycle.cyclesRemaining > 0 && (
              <Text size="xs" className="text-muted-foreground">
                {cycle.cyclesRemaining} more {cycle.cyclesRemaining === 1 ? 'cycle' : 'cycles'} queued
              </Text>
            )}
            {isActive && <CancelCycleButton cycleId={cycle.id} />}
          </div>
        }
      />

      <div className="space-y-6">
        {isStale && (
          <Banner variant="warning">
            <div className="flex items-center gap-2">
              <WarningCircle size={16} />
              <Text size="sm">
                This cycle has been running for {formatDurationFromIso(cycle.startedAt)} — longer than usual.
              </Text>
            </div>
          </Banner>
        )}

        {cycle.failureReason && (
          <Banner variant={cycle.status === 'failed' ? 'error' : 'warning'}>
            <Text size="sm">{cycle.failureReason}</Text>
          </Banner>
        )}

        <Card>
          <CardContent className="divide-y divide-border px-4 py-0">
            <MetadataItem label="Application">{propertyName ?? '—'}</MetadataItem>
            <MetadataItem label="Started">{formatDate(cycle.startedAt)}</MetadataItem>
            <MetadataItem label={isActive ? 'Running for' : 'Took'}>
              {formatDurationFromIso(cycle.startedAt, cycle.completedAt)}
            </MetadataItem>
          </CardContent>
        </Card>

        <ListCard title="Explorers" titleAccessory={`${doneCount} of ${cycle.workersPerCycle} done`}>
          {cycle.assignments.length === 0 ? (
            <div className="px-4 py-6">
              <Text size="sm" className="text-muted-foreground">
                {isActive ? 'No workers dispatched yet.' : 'No workers were dispatched.'}
              </Text>
            </div>
          ) : (
            cycle.assignments.map((assignment, index) => (
              <AssignmentRow key={assignment.runId} assignment={assignment} index={index} />
            ))
          )}
        </ListCard>

        {cycle.summaryMd && (
          <Card>
            <CardContent className="space-y-2 p-4">
              <Text size="sm" weight="semibold">
                Summary
              </Text>
              <div className="prose prose-sm max-w-none">
                <Markdown>{cycle.summaryMd}</Markdown>
              </div>
            </CardContent>
          </Card>
        )}

        {cycle.dispatchRationaleMd && (
          <Card>
            <CardContent className="space-y-2 p-4">
              <Text size="sm" weight="semibold">
                Dispatch rationale
              </Text>
              <div className="prose prose-sm max-w-none">
                <Markdown>{cycle.dispatchRationaleMd}</Markdown>
              </div>
            </CardContent>
          </Card>
        )}

        <ListCard title="Map changes" titleAccessory={cycle.mapChanges.length > 0 ? cycle.mapChanges.length : undefined}>
          {cycle.mapChanges.length === 0 ? (
            <div className="px-4 py-6">
              <Text size="sm" className="text-muted-foreground">
                No map changes.
              </Text>
            </div>
          ) : (
            cycle.mapChanges.map((change, index) => (
              <ListCardRow
                key={index}
                index={index}
                title={change.title}
                badges={
                  change.code ? (
                    <Link
                      to="/product-map"
                      search={{ propertyId: cycle.propertyId }}
                      className="font-mono text-xs text-primary hover:underline"
                    >
                      {change.code}
                    </Link>
                  ) : undefined
                }
                meta={
                  <>
                    <Text size="xs">{change.change}</Text>
                    {change.refusedFields && change.refusedFields.length > 0 && (
                      <Text size="xs">refused: {change.refusedFields.join(', ')}</Text>
                    )}
                  </>
                }
              />
            ))
          )}
        </ListCard>

        <ListCard title="Leads discovered" titleAccessory={cycle.leads.length > 0 ? cycle.leads.length : undefined}>
          {cycle.leads.length === 0 ? (
            <div className="px-4 py-6">
              <Text size="sm" className="text-muted-foreground">
                No leads discovered this cycle.
              </Text>
            </div>
          ) : (
            cycle.leads.map((lead, index) => (
              <ListCardRow
                key={lead.id}
                index={index}
                title={lead.label}
                badges={<TagBadge color={leadStatusColor[lead.status] ?? 'gray'} size="sm" name={lead.status} />}
                meta={
                  <>
                    <Text size="xs" className="font-mono truncate">
                      {lead.url}
                    </Text>
                    {lead.why && <Text size="xs">{lead.why}</Text>}
                  </>
                }
              />
            ))
          )}
        </ListCard>
      </div>
    </Page>
  );
}
